import { audio, type AudioEngine } from "./engine.ts";
import { ANNOUNCER, type LineId } from "./lines.ts";

const url = (id: LineId) => `/assets/audio/vo/${id}.mp3`;

interface Pending {
  id: LineId;
  priority: number;
  at: number;
}

/**
 * The Foundry's PA. One line at a time; newer or more important news replaces what's waiting,
 * and anything that has waited too long is dropped rather than read out late.
 */
export class Announcer {
  private queue: Pending[] = [];
  private busyUntil = 0;
  /** A line is loading/starting; its completion pumps the queue again. */
  private loading = false;
  private last = new Map<LineId, number>();
  private timer?: ReturnType<typeof setTimeout>;

  constructor(private readonly engine: AudioEngine = audio) {}

  get speaking() {
    return this.loading || performance.now() / 1000 < this.busyUntil;
  }

  preload() {
    for (const id of Object.keys(ANNOUNCER.lines) as LineId[]) void this.engine.buffer(url(id));
  }

  /**
   * Queue a line. `cooldown` suppresses repeats of the same line within that many seconds.
   * Priority: 3 chatter (contested) · 5 capture progress · 7 ownership · 8 overtime · 9 results.
   */
  say(id: LineId, priority = 5, cooldown = 0) {
    const now = performance.now() / 1000;
    if (cooldown > 0 && now - (this.last.get(id) ?? -1e9) < cooldown) return;
    this.last.set(id, now);
    this.queue = this.queue.filter((q) => q.id !== id && now - q.at < 2.5 && q.priority >= priority - 2);
    this.queue.push({ id, priority, at: now });
    this.queue.sort((a, b) => b.priority - a.priority || a.at - b.at);
    this.pump();
  }

  clear() {
    this.queue = [];
  }

  private pump(): void {
    clearTimeout(this.timer);
    if (this.loading) return;
    const now = performance.now() / 1000;
    if (now < this.busyUntil) {
      this.timer = setTimeout(() => this.pump(), (this.busyUntil - now) * 1000 + 20);
      return;
    }
    const next = this.queue.shift();
    if (!next) return;
    if (now - next.at > 2.5) {
      this.pump();
      return;
    }
    // Hold the channel until the sample has loaded and started, however long that takes.
    this.loading = true;
    const done = (dur: number) => {
      this.loading = false;
      const t = performance.now() / 1000;
      this.busyUntil = t + dur + 0.18;
      if (dur > 0) this.engine.duck(next.priority >= 8 ? 7 : 5, dur + 0.1);
      this.pump();
    };
    this.engine.sample({ bus: "voice", priority: 9 }, url(next.id)).then(done, () => done(0));
  }
}
